import { useEffect } from 'react';
import { useState } from 'react';
import AppWrappedSearchableAutocomplete from '../../AppWrappedSearchableAutocomplete';
import { fetchDepartmentData } from '../_api';

/**
 * Default Department input component for Epic/EpicCare stylized to work with Custom App Request Form
 * @param {Object} props properties
 * @returns Custom input
 */
const DefaultDepartmentField = ({ setDepartments, ...props }) => {
	const [deptOptions, setDeptOptions] = useState({});

	const DEPARTMENT_API_FAILURE_MESSAGE =
		'Unable to find any departments based on your input.  Please try again.';

	useEffect(() => {
		setDepartments(deptOptions);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [deptOptions]);

	/**
	 * Take user input and return a list of search results
	 * @param {string} value user input to search for
	 * @returns {string[]|object[]} list of results based on `value`
	 */
	const onSearchHandler = async value => {
		if (value) {
			const results = await fetchDepartmentData(value);
			if (typeof results === 'object') {
				const options = Object.keys(results).map(key => ({
					key: key,
					value: results[key].departmentId,
				}));
				setDeptOptions(results);
				return options.length > 200 ? options.slice(0, 200) : options;
			}
		}
		setDeptOptions({});
		return [];
	};

	/**
	 * Process search results
	 * @param {string[]|object[]} resultsArr list of search results
	 * @returns {object[]} array of `key`, `value` pairs based on `resultsArr`
	 */
	const onSearchSuccess = resultsArr => {
		if (Array.isArray(resultsArr)) {
			return resultsArr;
		} else if (typeof resultsArr === 'object') {
			return Object.keys(resultsArr).map(key => ({
				key: key,
				value: resultsArr[key],
			}));
		}
	};

	/**
	 * Gracefully handle errors during `onSearch` execution of `AppWrappedSearchableAutocomplete`
	 * @param {object} error error caught during `onSearch` workflow
	 * @returns {string[]} default list of results
	 */
	const onSearchFailure = error => {
		props.formik.setStatus({
			error: {
				source: props.name,
				cause: 'Input Failure',
				details: error,
				message: DEPARTMENT_API_FAILURE_MESSAGE,
			},
		});
		return [];
	};

	return (
		<AppWrappedSearchableAutocomplete
			label='Default Department'
			sublabel='Type to search by ID, Name, or Specialty.'
			helperText='Please select the default department that the user will log into.'
			onSearchHandler={onSearchHandler}
			onSearchFailure={onSearchFailure}
			onSearchSuccess={onSearchSuccess}
			{...props}
		/>
	);
};

export default DefaultDepartmentField;
